import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Search, Filter, Users, UserCheck, UserX, UserMinus } from 'lucide-react';
import { workerService } from '../../services/firebase';
import { Worker, WorkerStats } from '../../types';
import WorkerCard from './WorkerCard';
import WorkerSearch from './WorkerSearch';
import WorkerFilters from './WorkerFilters';

const WorkerManagement: React.FC = () => {
  const [workers, setWorkers] = useState<Worker[]>([]);
  const [filteredWorkers, setFilteredWorkers] = useState<Worker[]>([]);
  const [stats, setStats] = useState<WorkerStats>({
    totalWorkers: 0,
    activeWorkers: 0,
    inactiveWorkers: 0,
    workersOnLeave: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    status: '',
    gender: ''
  });

  useEffect(() => {
    loadWorkers();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [workers, searchTerm, filters]);

  const loadWorkers = async () => {
    try {
      setLoading(true);
      setError(null);
      const [workersData, statsData] = await Promise.all([
        workerService.getAllWorkers(),
        workerService.getWorkerStats()
      ]);
      setWorkers(workersData);
      setStats(statsData);
    } catch (err) {
      setError('Failed to load workers. Please try again.');
      console.error('Error loading workers:', err);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = workers;

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(worker =>
        worker.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        worker.phoneNumber.includes(searchTerm)
      );
    }

    // Apply status filter
    if (filters.status) {
      filtered = filtered.filter(worker => worker.availabilityStatus === filters.status);
    }

    // Apply gender filter
    if (filters.gender) {
      filtered = filtered.filter(worker => worker.gender === filters.gender);
    }

    setFilteredWorkers(filtered);
  };

  const handleSearch = (term: string) => {
    setSearchTerm(term);
  };

  const handleFilterChange = (newFilters: { status: string; gender: string }) => {
    setFilters(newFilters);
  };

  const handleWorkerUpdate = async (workerId: string, updates: Partial<Worker>) => {
    try {
      await workerService.updateWorker(workerId, updates);
      await loadWorkers(); // Reload to get updated data
    } catch (err) {
      setError('Failed to update worker. Please try again.');
      console.error('Error updating worker:', err);
    }
  };

  const handleWorkerDeactivate = async (workerId: string) => {
    if (window.confirm('Are you sure you want to deactivate this worker?')) {
      try {
        await workerService.deactivateWorker(workerId);
        await loadWorkers(); // Reload to get updated data
      } catch (err) {
        setError('Failed to deactivate worker. Please try again.');
        console.error('Error deactivating worker:', err);
      }
    }
  };

  const handleWorkerDelete = async (workerId: string) => {
    if (window.confirm('Are you sure you want to permanently delete this worker? This action cannot be undone.')) {
      try {
        await workerService.deleteWorker(workerId);
        await loadWorkers(); // Reload to get updated data
      } catch (err) {
        setError('Failed to delete worker. Please try again.');
        console.error('Error deleting worker:', err);
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8 bg-gray-50 dark:bg-gray-900 min-h-screen transition-colors duration-200">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-500 to-indigo-600 rounded-2xl p-8 text-white shadow-xl">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold mb-2">Worker Management</h1>
            <p className="text-indigo-100 text-lg">Manage your polyhouse workers and their information</p>
          </div>
          <Link
            to="/workers/add"
            className="inline-flex items-center px-6 py-3 border border-white/20 text-sm font-medium rounded-xl shadow-sm text-white bg-white/10 hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-white/50 transition-all backdrop-blur-sm"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add New Worker
          </Link>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Users className="h-6 w-6 text-gray-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Total Workers</dt>
                  <dd className="text-lg font-medium text-gray-900">{stats.totalWorkers}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <UserCheck className="h-6 w-6 text-green-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Active Workers</dt>
                  <dd className="text-lg font-medium text-gray-900">{stats.activeWorkers}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <UserMinus className="h-6 w-6 text-yellow-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">On Leave</dt>
                  <dd className="text-lg font-medium text-gray-900">{stats.workersOnLeave}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <UserX className="h-6 w-6 text-red-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Inactive</dt>
                  <dd className="text-lg font-medium text-gray-900">{stats.inactiveWorkers}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0 lg:space-x-4">
          <WorkerSearch onSearch={handleSearch} />
          <WorkerFilters filters={filters} onFilterChange={handleFilterChange} />
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{error}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Workers List */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          {filteredWorkers.length === 0 ? (
            <div className="text-center py-12">
              <Users className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No workers found</h3>
              <p className="mt-1 text-sm text-gray-500">
                {searchTerm || filters.status || filters.gender 
                  ? 'Try adjusting your search or filters.'
                  : 'Get started by adding a new worker.'
                }
              </p>
              {!searchTerm && !filters.status && !filters.gender && (
                <div className="mt-6">
                  <Link
                    to="/workers/add"
                    className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add New Worker
                  </Link>
                </div>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {filteredWorkers.map((worker) => (
                <WorkerCard
                  key={worker.id}
                  worker={worker}
                  onUpdate={handleWorkerUpdate}
                  onDeactivate={handleWorkerDeactivate}
                  onDelete={handleWorkerDelete}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WorkerManagement; 